import { hashKey, type QueryClient, type QueryKey } from '@tanstack/react-query';
import type { MailApi } from '@/api/contract';
import { ApiError } from '@/api/http';
import type { ConversationSortOrder } from '@/api/types';
import {
  applyConversationSync,
  compareConversations,
  conversationSyncRows,
  conversationSyncState,
  removeMessagesFromConversations,
  sameConversation,
  type ConversationListPage,
  type ConversationPageCache,
} from '@/cache/conversationPatches';
import { pendingRemovals } from '@/hooks/useMessageMutations';
import { clearArrivals } from '@/realtime/arrivals';
import { refetchInPlace } from '@/realtime/apply';
import { lanesHaveMore, type UnifiedLanes } from './unifiedList';
import type { ConversationSummary } from '@/api/types';

/**
 * The live path of a conversation list (ADR 0077, plan/16 §5 and §8).
 *
 * A frame that can have changed a listed folder (`new_mail`, `flags`,
 * `expunge`, `resync_required`) schedules ONE `POST …/conversations/sync` for
 * the list — never a refetch of its pages. On the owner's 21,500-message Inbox
 * (HANDOFF #350) a refetch per frame was a list that flashed on every click;
 * a sync costs what changed, and its answer is applied in place:
 *
 * - **Debounced** (300 ms): a burst of frames is one request.
 * - **One in flight, one trailing**, as `refetchInPlace` does: a frame during
 *   a sync asks for exactly one more after it, however many arrive — the one
 *   in flight may have been answered before the change.
 * - **Never under a page fetch.** A page fetch writes back the pages it
 *   started from plus its own, over anything set meanwhile. So a sync waits
 *   for a fetch that is out, and an answer that lands while one started is
 *   not applied: the list syncs once more after it (from the same token, so
 *   nothing is skipped).
 * - **Nothing dropped.** A frame before the first page has landed syncs once
 *   it has; a sync that failed for any reason but `409` is tried once more
 *   after a pause.
 * - `reset` falls back to that same in-place refetch; `409 folder_invalidated`
 *   drops the list, as it always has.
 *
 * React-free and keyed by `QueryClient`, so two mounts of one list (or a list
 * and a test) share one schedule rather than syncing twice.
 */

export const CONVERSATION_SYNC_DEBOUNCE_MS = 300;

/** The pause before the one retry of a sync that failed (not a `409`). */
export const CONVERSATION_SYNC_RETRY_MS = 2000;

/** One folder of one account that the list draws from. */
export interface ConversationLane {
  accountId: string;
  folderId: string;
}

export interface ConversationSyncTarget {
  queryKey: QueryKey;
  /** One for a folder's list; one per mailbox for the merged list. */
  lanes: readonly ConversationLane[];
  /** The merged list: rows and tokens are per lane (account). */
  unified: boolean;
  sort: ConversationSortOrder;
  unreadOnly?: boolean;
  flaggedOnly?: boolean;
}

/** A merged page carries its lanes; a folder's page carries `nextCursor`. */
type SyncedPage = ConversationListPage & { lanes?: UnifiedLanes<ConversationSummary> };

interface Schedule {
  target: ConversationSyncTarget;
  timer: ReturnType<typeof setTimeout> | undefined;
  running: boolean;
  again: boolean;
  /** A sync asked for before the list had a page, waiting for its first one. */
  waiting: boolean;
}

const schedules = new WeakMap<QueryClient, Map<string, Schedule>>();

function scheduleFor(client: QueryClient, target: ConversationSyncTarget): Schedule {
  let perClient = schedules.get(client);
  if (!perClient) {
    perClient = new Map();
    schedules.set(client, perClient);
  }
  const id = hashKey(target.queryKey);
  let schedule = perClient.get(id);
  if (!schedule) {
    schedule = { target, timer: undefined, running: false, again: false, waiting: false };
    perClient.set(id, schedule);
  }
  // The latest description wins: the lanes of a merged list can change.
  schedule.target = target;
  return schedule;
}

/**
 * Asks for a sync of this list. Coalesces: within the debounce window it is
 * the one already scheduled; during a sync it is one more after it.
 */
export function scheduleConversationSync(
  client: QueryClient,
  api: MailApi,
  target: ConversationSyncTarget,
): void {
  const schedule = scheduleFor(client, target);
  if (schedule.running) {
    schedule.again = true;
    return;
  }
  if (schedule.timer !== undefined) return;
  schedule.timer = setTimeout(() => {
    schedule.timer = undefined;
    void drain(client, api, schedule);
  }, CONVERSATION_SYNC_DEBOUNCE_MS);
}

async function drain(client: QueryClient, api: MailApi, schedule: Schedule): Promise<void> {
  schedule.running = true;
  try {
    do {
      schedule.again = false;
      const outcome = await runConversationSync(client, api, schedule.target);
      if (outcome === 'stale') schedule.again = true;
      else if (outcome === 'unloaded') syncWhenLoaded(client, api, schedule);
    } while (schedule.again);
  } finally {
    schedule.running = false;
  }
}

/**
 * A change signalled before the list's first page landed: that page may have
 * been read before the change, so the list syncs once it is there. One wait
 * per list, however many signals arrive meanwhile.
 */
function syncWhenLoaded(client: QueryClient, api: MailApi, schedule: Schedule): void {
  if (schedule.waiting) return;
  schedule.waiting = true;
  const hash = hashKey(schedule.target.queryKey);
  const unsubscribe = client.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated' || event.query.queryHash !== hash) return;
    const data = event.query.state.data as ConversationPageCache | undefined;
    if (!data || data.pages.length === 0) return;
    unsubscribe();
    schedule.waiting = false;
    scheduleConversationSync(client, api, schedule.target);
  });
}

function isFetching(client: QueryClient, queryKey: QueryKey): boolean {
  return client.getQueryState(queryKey)?.fetchStatus === 'fetching';
}

/** Resolves once no fetch of this list is out (at once, when none is). */
function fetchSettled(client: QueryClient, queryKey: QueryKey): Promise<void> {
  const query = client.getQueryCache().find({ queryKey, exact: true });
  if (!query || query.state.fetchStatus !== 'fetching') return Promise.resolve();
  return new Promise((resolve) => {
    const unsubscribe = client.getQueryCache().subscribe((event) => {
      if (event.query !== query) return;
      if (event.type !== 'removed' && query.state.fetchStatus === 'fetching') return;
      unsubscribe();
      resolve();
    });
  });
}

function pause(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function hasMoreBelow(cache: ConversationPageCache | undefined, unified: boolean): boolean {
  const last = cache?.pages[cache.pages.length - 1] as SyncedPage | undefined;
  if (!last) return false;
  return unified ? (last.lanes ? lanesHaveMore(last.lanes) : false) : last.nextCursor !== undefined;
}

/**
 * The merged list keeps rows it has fetched but not yet shown in its lanes'
 * buffers, and each lane's cursor is already past them. So, for the lane
 * this answer is for:
 *
 * - a row this sync placed on screen (or removed) leaves the buffer, or the
 *   next page would show a stale second copy;
 * - a changed row still waiting there is swapped for its new value, so it
 *   arrives current;
 * - an upsert that sorts past what is on screen but within what the LANE has
 *   fetched (before its buffered tail, or anywhere when the lane is
 *   exhausted) joins the buffer — its cursor will never bring it again, and
 *   the global "more below" the patch decided on cannot know that. One past
 *   the lane's tail comes with the lane's next page, as in a folder's list.
 */
function reconcileLaneBuffers(
  cache: ConversationPageCache,
  laneAccountId: string,
  sort: ConversationSortOrder,
  upserts: readonly ConversationSummary[],
  removed: readonly string[],
): ConversationPageCache {
  const lastIndex = cache.pages.length - 1;
  const last = cache.pages[lastIndex] as SyncedPage | undefined;
  const lane = last?.lanes?.[laneAccountId];
  if (!last || !lane || lane.failure) return cache;
  const compare = compareConversations(sort);
  const shown = new Set<string>();
  for (const page of cache.pages) {
    for (const row of page.items) if (row.accountId === laneAccountId) shown.add(row.id);
  }
  const gone = new Set(removed);
  const fresh = new Map<string, ConversationSummary>();
  for (const row of upserts) if (row.accountId === laneAccountId) fresh.set(row.id, row);

  let changed = false;
  const buffer: ConversationSummary[] = [];
  const buffered = new Set<string>();
  for (const row of lane.buffer) {
    if (gone.has(row.id) || shown.has(row.id)) {
      changed = true;
      continue;
    }
    const next = fresh.get(row.id);
    const kept = next !== undefined && !sameConversation(next, row) ? next : row;
    if (kept !== row) changed = true;
    buffer.push(kept);
    buffered.add(row.id);
  }
  // The tail the lane has fetched to: past it, its cursor still has the row.
  const tail = lane.buffer[lane.buffer.length - 1];
  for (const row of fresh.values()) {
    if (shown.has(row.id) || buffered.has(row.id) || gone.has(row.id)) continue;
    if (!lane.exhausted && (tail === undefined || compare(row, tail) >= 0)) continue;
    buffer.push(row);
    changed = true;
  }
  if (!changed) return cache;
  buffer.sort(compare);
  const pages = [...cache.pages];
  pages[lastIndex] = {
    ...last,
    lanes: { ...last.lanes, [laneAccountId]: { ...lane, buffer } },
  } as SyncedPage;
  return { ...cache, pages };
}

/**
 * What one sync of a list came to:
 * - `done`: applied, or nothing to do;
 * - `unloaded`: the list has no page yet (see `syncWhenLoaded`);
 * - `stale`: a page fetch started while the answer was out, so it was not
 *   applied — sync again once the fetch has landed;
 * - `stop`: the list was reset or refetched instead.
 */
export type ConversationSyncOutcome = 'done' | 'unloaded' | 'stale' | 'stop';

/**
 * One sync of every lane of a list, the lanes side by side, each applied as
 * its answer lands.
 */
export async function runConversationSync(
  client: QueryClient,
  api: MailApi,
  target: ConversationSyncTarget,
): Promise<ConversationSyncOutcome> {
  const outcomes = await Promise.all(target.lanes.map((lane) => syncLane(client, api, target, lane)));
  for (const outcome of ['stop', 'stale', 'unloaded'] as const) {
    if (outcomes.includes(outcome)) return outcome;
  }
  return 'done';
}

async function syncLane(
  client: QueryClient,
  api: MailApi,
  target: ConversationSyncTarget,
  lane: ConversationLane,
): Promise<ConversationSyncOutcome> {
  const { queryKey, unified } = target;
  const scope = unified ? lane.accountId : undefined;
  for (let attempt = 0; ; attempt += 1) {
    // Rows and a token read mid-fetch would be overwritten by the fetch.
    await fetchSettled(client, queryKey);
    const cache = client.getQueryData<ConversationPageCache>(queryKey);
    if (!cache || cache.pages.length === 0) return 'unloaded';
    const state = conversationSyncState(cache, scope);
    // A lane that never answered (a failed mailbox) has nothing to sync from.
    if (state === undefined) return 'done';

    let result;
    try {
      result = await api.messages.syncConversations(lane.accountId, lane.folderId, {
        state,
        rows: conversationSyncRows(cache, scope),
        ...(target.unreadOnly ? { unreadOnly: true } : {}),
        ...(target.flaggedOnly ? { flaggedOnly: true } : {}),
      });
    } catch (error) {
      if (error instanceof ApiError && error.isFolderInvalidated) {
        // Every id the list holds names something else now: drop it and
        // start again, exactly as the message list does.
        // Reset, not removed: a removed query its observer still holds would
        // never be refetched (see `discardFolder` in realtime/apply.ts).
        void client.resetQueries({ queryKey, exact: true });
        return 'stop';
      }
      // A sync that did not happen (a timeout, a dropped connection): once
      // more after a pause, rather than waiting for a frame that may not come.
      if (attempt === 0) {
        await pause(CONVERSATION_SYNC_RETRY_MS);
        continue;
      }
      return 'done';
    }

    if (result.reset) {
      // More changed than a patch can say: the loaded pages, refetched in place.
      refetchInPlace(client, queryKey);
      return 'stop';
    }

    // A page fetch started while this was out would write back the pages it
    // started from over this answer. Leave the token where it was and sync
    // again after it: that sync re-reports everything this one would have.
    if (isFetching(client, queryKey)) return 'stale';

    const current = client.getQueryData<ConversationPageCache>(queryKey);
    let next = applyConversationSync(current, result, {
      sort: target.sort,
      hasMore: hasMoreBelow(current, unified),
      ...(scope !== undefined ? { laneAccountId: scope } : {}),
    });
    // An archive still under its Undo has not reached the server, so the
    // server still reports those conversations. They stay out.
    for (const pending of pendingRemovals()) {
      if (pending.accountId !== lane.accountId) continue;
      next = removeMessagesFromConversations(next, pending.messageIds, scope);
    }
    if (next && scope !== undefined) {
      next = reconcileLaneBuffers(next, scope, target.sort, result.upserts, result.removed);
    }
    // An answer that changed nothing writes nothing: no observer is told of a
    // "new" list that is the old one.
    if (next !== current && next !== undefined) client.setQueryData(queryKey, next);
    // The list now holds what arrived; nothing is waiting behind a pill.
    clearArrivals(lane.accountId, lane.folderId);
    return 'done';
  }
}
